// Per-side presentation for the A/B comparison. The two sides can be shown
// differently: Option A uses the block's own presentation features, Option B
// uses the _b variant settings. This mirrors the desktop ABTestingDialog +
// _PanelAnimator: each side has an optional pre-reveal blank delay
// (delay_before_send), an optional typing-indicator wait, then a reveal that is
// streamed, typed, or instant. The dialog syncs both sides so they reveal at
// the same moment, matching the desktop.

export function ptToPx(pt: number): number {
  return Math.round((pt * 4) / 3);
}

// When neither side has any delay or typing wait, both still reveal after this
// "thinking" delay (desktop _PanelAnimator default response_delay_ms = 2000).
export const DEFAULT_AB_REVEAL_DELAY_MS = 2000;

// Default chat bubble size when TEXT_SIZE_CHANGER is off (matches the main chat).
const DEFAULT_BUBBLE_FONT_PX = 30;
const TYPING_FLAGS = ["TYPING_INDICATOR", "THINKING", "ELLIPSES"];

export type AbPaneRaw = {
  typing: boolean;
  // The typing-indicator hold, already floored at the desktop minimum (1500ms);
  // 0 when this side has no typing indicator.
  typingWaitMs: number;
  typingStyle: string;
  typingLabel: string;
  delayBeforeSendMs: number; // pre-reveal blank (delay_before_send)
  streaming: boolean;
  typewriter: boolean;
  typewriterSpeedMs: number;
  fontPx: number;
};

function num(v: unknown, d: number): number {
  return typeof v === "number" ? v : d;
}
function str(v: unknown, d: string): string {
  return typeof v === "string" && v ? v : d;
}

// Desktop: wait = max(configured typing duration, 1500) when typing is on.
function typingWait(on: boolean, durationMs: number): number {
  return on ? Math.max(durationMs, 1500) : 0;
}

// Option A: the block's live presentation features/settings. Streaming wins over
// typewriter, matching the main chat.
export function deriveOptionAConfig(
  settings: Record<string, unknown>,
  features: Record<string, boolean> | undefined,
): AbPaneRaw {
  const f = features ?? {};
  const streaming = f["STREAMING"] === true;
  const typing = TYPING_FLAGS.some((k) => f[k] === true);
  return {
    typing,
    typingWaitMs: typingWait(typing, num(settings.typing_indicator_min_duration_s, 0) * 1000),
    typingStyle: str(settings.typing_indicator_style, "dots"),
    typingLabel: str(settings.typing_indicator_label, "AI is typing"),
    delayBeforeSendMs: f["DELAY_BEFORE_SEND"] === true ? num(settings.delay_seconds, 0) * 1000 : 0,
    streaming,
    typewriter: f["TYPEWRITER"] === true && !streaming,
    typewriterSpeedMs: Math.max(1, num(settings.typewriter_speed_ms, 20)),
    fontPx:
      f["TEXT_SIZE_CHANGER"] === true ? ptToPx(num(settings.text_size, 20)) : DEFAULT_BUBBLE_FONT_PX,
  };
}

// Option B: the _b variant settings (ab_*_b).
export function deriveOptionBConfig(settings: Record<string, unknown>): AbPaneRaw {
  const streaming = settings.ab_streaming_b === true;
  const typing = settings.ab_typing_indicator_b === true;
  return {
    typing,
    typingWaitMs: typingWait(typing, num(settings.typing_indicator_min_duration_s_b, 0) * 1000),
    typingStyle: str(settings.typing_indicator_style, "dots"),
    typingLabel: str(settings.typing_indicator_label, "AI is typing"),
    delayBeforeSendMs:
      settings.ab_delay_before_send_b === true ? num(settings.delay_seconds_b, 0) * 1000 : 0,
    streaming,
    typewriter: settings.ab_typewriter_b === true && !streaming,
    typewriterSpeedMs: Math.max(1, num(settings.typewriter_speed_ms_b, 20)),
    fontPx:
      settings.ab_text_size_changer_b === true
        ? ptToPx(num(settings.text_size_b, 20))
        : DEFAULT_BUBBLE_FONT_PX,
  };
}

// Per-side pre-reveal blank delays so both sides reveal at the same moment
// (desktop sync: total = pre-delay + typing wait; the quicker side waits longer
// before starting so both land on max_total).
export function abRevealTimings(
  a: AbPaneRaw,
  b: AbPaneRaw,
  defaultDelayMs: number = DEFAULT_AB_REVEAL_DELAY_MS,
): { blankA: number; blankB: number } {
  const totalA = a.delayBeforeSendMs + a.typingWaitMs;
  const totalB = b.delayBeforeSendMs + b.typingWaitMs;
  if (totalA === 0 && totalB === 0) {
    return { blankA: defaultDelayMs, blankB: defaultDelayMs };
  }
  const maxTotal = Math.max(totalA, totalB);
  return { blankA: maxTotal - a.typingWaitMs, blankB: maxTotal - b.typingWaitMs };
}
